import { httpPostAuthToken } from '@iconik-io/iconik-api/src/services/auth';
import {
  type AutoLoginHeaders,
  OutdatedAgentError,
  type TryDownloadOptions,
  type TryDownloadRequestResponse,
} from '@iconik-io/shared-lib/src/agent/agent_api_client';
import { itemsToDownloadRequest } from '@iconik-io/shared-lib/src/agent/items_to_download_request';
import { describeDownloadRequest } from '@iconik-io/shared-lib/src/analytics/download_event';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import { agentApiClient } from '../../agent_api_client';
import DownloadAgentErrorMessage from '../../components/agent/download_agent_error_message';
import type { AppDispatch, GetState } from '../../store/types';
import { getHost } from '../app/selectors';
import { trackDownloadEvent } from '../files/tracking';
import { warningMessage } from '../messages/thunks';
import { selectIsSharePage } from '../share/selectors';

const makeLoggedInAuthHeaders =
  () =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AutoLoginHeaders | undefined> => {
    const state = getState();

    const authResponse = await dispatch(httpPostAuthToken())
      .then((response) => response.body)
      .catch(() => undefined);
    if (!authResponse) {
      return undefined;
    }
    if (!authResponse.app_id) {
      return undefined;
    }

    return {
      'Iconik-Host': getHost(state),
      'Auth-Token': authResponse.token,
      'App-ID': authResponse.app_id,
    };
  };

const makeShareAuthHeaders =
  () =>
  async (
    _dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AutoLoginHeaders | undefined> => {
    const state = getState();
    const hash = state.routeParams.query.hash;
    const appId = state.share.shareAuthLogin?.app_id;
    if (!hash || !appId) {
      return undefined;
    }

    return {
      'Iconik-Host': getHost(state),
      'Share-Hash': hash,
      'App-ID': appId,
    };
  };

export const makeAuthHeaders =
  () => async (dispatch: AppDispatch, getState: GetState) => {
    const isShare = selectIsSharePage(getState());
    let headers;
    if (isShare) {
      headers = await dispatch(makeShareAuthHeaders());
    } else {
      headers = await dispatch(makeLoggedInAuthHeaders());
    }
    return headers;
  };

export const openInFcp =
  (searchItems: SearchItemEntity[]) => (dispatch: AppDispatch) =>
    dispatch(
      agentTryDownload({
        body: itemsToDownloadRequest(searchItems, {}, 'original'),
        params: {
          open_in_fcp: 1,
        },
      }),
    );

export const agentTryDownload =
  ({
    formats,
    ...options
  }: TryDownloadOptions & {
    // File set requests carry no format, so the caller supplies what it asked for
    // This is only used for tracking purposes
    formats?: readonly string[];
  }) =>
  async (dispatch: AppDispatch): Promise<TryDownloadRequestResponse> => {
    const headers = await dispatch(makeAuthHeaders());
    return await agentApiClient
      .tryDownload({
        autoLoginHeaders: headers,
        ...options,
      })
      .then((response) => {
        if (response.type === 'error') {
          const { error } = response;
          if (error instanceof OutdatedAgentError) {
            dispatch(
              warningMessage({
                text: DownloadAgentErrorMessage({
                  message: error.message,
                }),
                timeout: 10000,
              }),
            );
          } else {
            dispatch(
              warningMessage({
                text: error.message,
                autoclose: false,
              }),
            );
          }
        }
        if (response.type === 'success') {
          const request = describeDownloadRequest(options.body);
          dispatch(
            trackDownloadEvent({
              ...request,
              formats: request.formats ?? formats,
              handledBy: 'Agent',
            }),
          );
        }
        return response;
      });
  };
